import { createRoot } from 'react-dom/client';
import { v4 as uuidv4 } from 'uuid';

import { configureFileUpload } from '@gem/modules/file-upload';
import { configureTranslations, extractShortLanguage } from '@gem/modules/i18n';
import { setIsLogEnabled, setLoggerConfig } from '@gem/modules/logger';
import { configureRemoteStorage } from '@gem/modules/remote-storage';
import { configure as configureDate } from '@gem/services/date-service';
import { isWebPSupported, calculatePlatform } from '@gem/utils';

import 'APP/Tasks';
import { DEFAULT_MEDIA_PREVIEW_IMG } from 'APP/constants';
import { DEFAULT_LANGUAGE } from 'APP/constants/language';
import { fileStorageModel } from 'APP/model/model';
import { getUploadUrl } from 'APP/model/upload/uploadModel';
import { analyticsClient } from 'APP/packages/analytics/analytics';
import { ElectronApi } from 'APP/packages/electron/ElectronApi';
import { Features } from 'APP/packages/features/Features';
import { ProjectUrls } from 'APP/packages/projectUrls/ProjectUrls';
import { storage } from 'APP/packages/storage';
import { setSseConfig } from 'APP/packages/streamApi';
import { configureRestClient } from 'APP/restApi/restClient/configureRestClient';
import { getLanguage } from 'UTILS/getLanguage';
import { getTranslationStrings } from 'UTILS/getTranslationStrings';

import { BugsnagWrapper } from './components/BugsnagWrapper/BugsnagWrapper';
import './main.css';
import { registerSW } from './registerSW';
import { AppRouter } from './router/AppRouter';
import { setWebsocketConfig } from './wsApi/wsApi.config';

window.app = {
  version: `${VERSION}` + (BUILD_NUMBER ? `.${BUILD_NUMBER}` : ''),
  hash: HASH_COMMIT,
};

const renderApp = (): void => {
  const container = document.getElementById('root');
  const root = createRoot(container!);

  setIsLogEnabled(Features.isLogEnabled());

  if (!Features.isBugsnagEnabled()) {
    root.render(<AppRouter />);
  } else {
    root.render(
      <BugsnagWrapper>
        <AppRouter />
      </BugsnagWrapper>
    );
  }
};

setWebsocketConfig({
  apiUrl: ProjectUrls.wsApiUrlDeprecated,
});

setSseConfig({
  apiUrl: ProjectUrls.streamApiUrl,
});

setLoggerConfig({
  enabled: Features.isLogEnabled(),
});

configureRestClient();

configureFileUpload({
  strategy: 'presigned-url',
  getUploadUrlData: getUploadUrl,
  defaultPreviewImage: DEFAULT_MEDIA_PREVIEW_IMG,
});

configureRemoteStorage({
  getCredentialsRequest: fileStorageModel.getStorageSessionCredentials,
});

let language = storage.language.get();
if (!language) {
  language = getLanguage();
  storage.language.set(extractShortLanguage(language));
}
configureDate({ language });

analyticsClient.initialize({
  amplitudeApiKey: PROJECT_CONFIG.amplitudeApiKey,
  gtmId: PROJECT_CONFIG.gtmId,
});

registerSW();

const promises = [];

promises.push(
  getTranslationStrings(language).then((strings) => {
    return configureTranslations({
      strings,
      language,
      defaultLanguage: DEFAULT_LANGUAGE,
    });
  })
);

promises.push(
  (async function (): Promise<void> {
    window.supportsWEBP = await isWebPSupported();
  })()
);

promises.push(
  ((): Promise<void> => {
    let deviceId = storage.deviceId.get();
    if (!deviceId) {
      deviceId = uuidv4();
      storage.deviceId.set(deviceId);
    }
    return calculatePlatform(window.navigator.userAgent, deviceId);
  })()
);

Promise.all(promises).then(() => {
  renderApp();
  ElectronApi.hideLoader();
});
